/**
 * Import Service
 *
 * Handles all import-related API calls.
 */

import api from './api';
import type {
  ApiResponse,
  ParseCsvRequest,
  ParseCsvResponse,
  PreviewImportRequest,
  PreviewImportResponse,
  ExecuteImportRequest,
  ExecuteImportResponse,
  ImportMapping,
  SaveImportMappingRequest,
} from '@budgira/shared';

export const importService = {
  /**
   * Parse CSV content and detect settings
   */
  async parseCsv(request: ParseCsvRequest): Promise<ParseCsvResponse> {
    const response = await api.post<ApiResponse<ParseCsvResponse>>('/import/parse', request);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to parse CSV');
    }
    return response.data.data;
  },

  /**
   * Preview import with mapping applied
   */
  async previewImport(accountId: string, request: PreviewImportRequest): Promise<PreviewImportResponse> {
    const response = await api.post<ApiResponse<PreviewImportResponse>>(
      `/accounts/${accountId}/import/preview`,
      request
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to preview import');
    }
    return response.data.data;
  },

  /**
   * Execute the import
   * Note: Long timeout (5 minutes) for large files
   */
  async executeImport(accountId: string, request: ExecuteImportRequest): Promise<ExecuteImportResponse> {
    const response = await api.post<ApiResponse<ExecuteImportResponse>>(
      `/accounts/${accountId}/import/execute`,
      request,
      { timeout: 300000 } // 5 minutes timeout for large imports
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to execute import');
    }
    return response.data.data;
  },

  /**
   * Get import job status
   */
  async getJobStatus(jobId: string): Promise<ExecuteImportResponse> {
    const response = await api.get<ApiResponse<ExecuteImportResponse>>(`/import/jobs/${jobId}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get job status');
    }
    return response.data.data;
  },

  /**
   * Cancel an active import job
   */
  async cancelJob(jobId: string): Promise<void> {
    const response = await api.post<ApiResponse<{ cancelled: boolean }>>(`/import/jobs/${jobId}/cancel`);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to cancel job');
    }
  },

  /**
   * Get active import job if any
   */
  async getActiveJob(): Promise<ExecuteImportResponse | null> {
    const response = await api.get<ApiResponse<ExecuteImportResponse | null>>('/import/active-job');
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to get active job');
    }
    return response.data.data ?? null;
  },

  /**
   * Get all saved mappings
   */
  async getMappings(): Promise<ImportMapping[]> {
    const response = await api.get<ApiResponse<ImportMapping[]>>('/import/mappings');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get mappings');
    }
    return response.data.data;
  },

  /**
   * Get a specific mapping
   */
  async getMapping(id: string): Promise<ImportMapping> {
    const response = await api.get<ApiResponse<ImportMapping>>(`/import/mappings/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get mapping');
    }
    return response.data.data;
  },

  /**
   * Create a new mapping
   */
  async createMapping(data: SaveImportMappingRequest): Promise<ImportMapping> {
    const response = await api.post<ApiResponse<ImportMapping>>('/import/mappings', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create mapping');
    }
    return response.data.data;
  },

  /**
   * Update an existing mapping
   */
  async updateMapping(id: string, data: SaveImportMappingRequest): Promise<ImportMapping> {
    const response = await api.put<ApiResponse<ImportMapping>>(`/import/mappings/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update mapping');
    }
    return response.data.data;
  },

  /**
   * Delete a mapping
   */
  async deleteMapping(id: string): Promise<void> {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(`/import/mappings/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete mapping');
    }
  },
};

export default importService;
